import type { Cell } from '../schemas/cell.js';
import type { Dimension } from '../schemas/dimension.js';
import type { ExportSelection } from '../schemas/export.js';
import type { ResolvedPalier } from '../schemas/palier.js';
import type { SingularityLevier } from '../schemas/singularity-setup.js';
import type { BulletLine } from './parse-bullet-lines.js';
import { parseBulletLines } from './parse-bullet-lines.js';

/** The spanning group label the six activation rows share — the same body-row
 *  label the matrix grid renders in its `rowSpan`'d header cell (decision M3).
 *  Not a row key: the rows themselves are keyed by levier order. */
export const ACTIVATION_GROUP_LABEL = "Ligne d'activation";

export interface ExportModelColumn {
  number: number;
  label: string;
}

export interface ExportModelRow {
  key: string;
  kind: 'activation' | 'dimension';
  label: string;
}

export interface ExportModel {
  columns: ExportModelColumn[];
  rows: ExportModelRow[];
  /** `cellContent[row.key][column.number]` → that cell's bullet lines (empty array if no content). */
  cellContent: Record<string, Record<number, BulletLine[]>>;
}

function currentContent(cell: Cell | undefined): string {
  if (!cell || cell.currentVersionId === null) return '';
  return cell.versions.find((version) => version.id === cell.currentVersionId)?.content ?? '';
}

/** One levier's own bullets for one column — the export mirrors the matrix
 *  grid, which renders the activation block as six independent body rows. */
function levierBulletLines(cells: Cell[], order: number, columnNumber: number): BulletLine[] {
  const cell = cells.find(
    (entry) =>
      entry.rowType === 'activation' &&
      entry.rowRef === String(order) &&
      entry.columnNumber === columnNumber,
  );
  return parseBulletLines(currentContent(cell));
}

function dimensionBulletLines(
  cells: Cell[],
  dimensionId: string,
  columnNumber: number,
): BulletLine[] {
  const cell = cells.find(
    (entry) =>
      entry.rowType === 'dimension' &&
      entry.rowRef === dimensionId &&
      entry.columnNumber === columnNumber,
  );
  return parseBulletLines(currentContent(cell));
}

export function buildExportModel(input: {
  paliers: ResolvedPalier[];
  dimensions: Dimension[];
  cells: Cell[];
  /** The company's six leviers — their `order` keys the activation rows and their `levierValeur` labels them. */
  singularitySetup: SingularityLevier[];
  selection: ExportSelection;
}): ExportModel {
  const columns: ExportModelColumn[] = input.paliers
    .filter((palier) => input.selection.includedColumns.includes(palier.number))
    .sort((a, b) => a.number - b.number)
    .map((palier) => ({ number: palier.number, label: palier.label }));

  // Row keys '1'…'6' are levier orders and dimension keys are UUIDs, so the
  // two kinds can never collide in `cellContent`.
  const activationRows: ExportModelRow[] = input.selection.includeActivationRow
    ? [...input.singularitySetup]
        .sort((a, b) => a.order - b.order)
        .map((levier) => ({
          key: String(levier.order),
          kind: 'activation' as const,
          label: levier.levierValeur ?? `Levier ${levier.order}`,
        }))
    : [];

  const rows: ExportModelRow[] = [
    ...activationRows,
    ...input.dimensions
      .filter((dimension) => input.selection.includedDimensionIds.includes(dimension.id))
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((dimension) => ({
        key: dimension.id,
        kind: 'dimension' as const,
        label: dimension.title,
      })),
  ];

  const cellContent: Record<string, Record<number, BulletLine[]>> = {};
  for (const row of rows) {
    const rowContent: Record<number, BulletLine[]> = {};
    for (const column of columns) {
      rowContent[column.number] =
        row.kind === 'activation'
          ? levierBulletLines(input.cells, Number(row.key), column.number)
          : dimensionBulletLines(input.cells, row.key, column.number);
    }
    cellContent[row.key] = rowContent;
  }

  return { columns, rows, cellContent };
}
